import { CircleCheck, X, LoaderCircle, TriangleAlert } from 'lucide-react';

/**
 * A2F-565: confirmation dialog for bulk-accepting observed-but-unexpected
 * sections on a submission's Section Map.
 *
 * Visually modeled on CompletionDialog but deliberately NOT a variant — the
 * payload (section list + stale-banner) diverges from CompletionDialog's
 * (metrics + comment + reopen flag) enough that forcing a union would be
 * worse than a parallel component.
 */

export interface ObservedSectionRow {
  /** CTD section number, e.g. "3.2.P.5.1" or "1.11.1". */
  sectionNumber: string;
  /** Short module prefix from the server, e.g. "3" for M3. */
  module: string;
}

interface AcceptUnexpectedDialogProps {
  submissionLabel: string;
  /** Sections to accept, pre-sorted (module-grouped, natural order within). */
  sections: ObservedSectionRow[];
  /** In-flight flag for the primary button spinner + disabled state. */
  submitting: boolean;
  /** Generic error message (non-409). Cleared by caller on retry. */
  error: string;
  /** Stale-state 409 banner: sections that changed server-side since the
   *  dialog opened. Non-null → block the primary action and prompt refresh. */
  driftedSections: string[] | null;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}

/** Group a pre-sorted list into `[moduleKey, rows][]` preserving order. */
function groupByModule(rows: ObservedSectionRow[]): Array<[string, ObservedSectionRow[]]> {
  const map = new Map<string, ObservedSectionRow[]>();
  for (const r of rows) {
    const existing = map.get(r.module);
    if (existing) existing.push(r);
    else map.set(r.module, [r]);
  }
  return Array.from(map.entries());
}

export function AcceptUnexpectedDialog({
  submissionLabel,
  sections,
  submitting,
  error,
  driftedSections,
  onConfirm,
  onClose,
}: AcceptUnexpectedDialogProps) {
  const count = sections.length;
  const groups = groupByModule(sections);
  const disabled = submitting || count === 0 || driftedSections !== null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <CircleCheck className="w-5 h-5 text-brand-orange" />
            <h2 className="text-lg font-bold text-brand-charcoal">
              Accept {count} unexpected {count === 1 ? 'section' : 'sections'}
            </h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-slate-600">
            {submissionLabel} has documents in {count} {count === 1 ? 'section' : 'sections'}
            {' '}the recommended-section map doesn't predict. Accepting moves them to this
            submission's manual section list. This does not change the submission type's
            recommended map; only this submission is affected.
          </p>

          {/* Stale-state banner — 409 response */}
          {driftedSections && driftedSections.length > 0 && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <TriangleAlert className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">These sections changed while you were reviewing:</p>
                <p className="font-mono text-xs">{driftedSections.join(', ')}</p>
                <p className="mt-1">Refresh the Section Map to see the current state.</p>
              </div>
            </div>
          )}

          {/* Section list, grouped by module */}
          <div className="bg-slate-50 rounded-lg p-4 max-h-64 overflow-y-auto space-y-3">
            {groups.map(([mod, rows]) => (
              <div key={mod}>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                  Module {mod}
                </p>
                <ul className="space-y-0.5">
                  {rows.map((r) => (
                    <li key={r.sectionNumber} className="font-mono text-xs text-slate-700">
                      {r.sectionNumber}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {error && !driftedSections && (
            <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={disabled}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-orange rounded-lg hover:bg-orange-600 disabled:opacity-50"
          >
            {submitting && <LoaderCircle className="w-4 h-4 animate-spin" />}
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
}
